import validator from 'validator';

const passwordWhitelist =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!()-.?{}_~;:@#$%^&*+=';
const passwordBlacklistRegex = /[^A-Za-z0-9!\(\)-\.\?{}_~;:@#\$%\^&\*\+=]/g;
const defaultMinLength = 8,
  defaultMaxLength = 32;

export function cleanPassword(password: string) {
  return validator
    .whitelist(password, passwordWhitelist)
    .replaceAll(passwordBlacklistRegex, '');
}

type PasswordFailureReasonsWithPointValues =
  | 'needs lowercase'
  | 'needs uppercase'
  | 'needs number'
  | 'needs symbol';

export type PasswordFailureReason =
  | PasswordFailureReasonsWithPointValues
  | 'too short'
  | 'too long'
  | 'blacklisted char';

// See the big comment in isValidPassword below
const passwordStrengthPointMap: {
  [K in PasswordFailureReasonsWithPointValues]: number;
} = {
  'needs lowercase': 1,
  'needs uppercase': 3,
  'needs number': 7,
  'needs symbol': 13,
};

// Ensures there are no blacklisted characters and it's not empty
// Used in the sign in page to make sure any future changes to password requirements won't
// prevent users from moving past the email sign in page
export function couldBePassword(password: string) {
  return (
    validator.isWhitelisted(password, passwordWhitelist) &&
    validator
      .whitelist(password, passwordWhitelist)
      .replaceAll(passwordBlacklistRegex, '').length > 0
  );
}

export function isValidPassword(
  password: string,
  minLength: number = defaultMinLength,
  maxLength: number = defaultMaxLength,
): boolean {
  return getPasswordFailures(password, minLength, maxLength).length === 0;
}

export function getPasswordFailures(
  password: string,
  minLength: number = defaultMinLength,
  maxLength: number = defaultMaxLength,
): PasswordFailureReason[] {
  const passwordFailures: PasswordFailureReason[] = [];

  if (
    password.length !==
      validator.whitelist(password, passwordWhitelist).length ||
    password.length !== password.replaceAll(passwordBlacklistRegex, '').length
  )
    passwordFailures.push('blacklisted char');

  if (password.length < minLength) passwordFailures.push('too short');
  else if (password.length > maxLength) passwordFailures.push('too long');

  let passwordStrength = validator.isStrongPassword(
    validator.whitelist(password, passwordWhitelist),
    {
      returnScore: true,
      minLength: minLength,
      minUppercase: 1,
      minLowercase: 1,
      minNumbers: 1,
      minSymbols: 1,
      pointsPerRepeat: 0,
      pointsPerUnique: 0,
      /* 
    // To not have to implement isStrongPassword ourselves but still extract information about what 
    // required element is missing from this password, we assign a specially-picked set of 4 primes
    // to the point values corresponding to each element in a way that any returned passwordStrength
    // (any sum of any combination of the primes) can *always* easily be deconstructed into the primes
    // that were summed together, letting us get the reason this password isn't secure without a custom
    // implementation
    */
      pointsForContainingLower: passwordStrengthPointMap['needs lowercase'],
      pointsForContainingUpper: passwordStrengthPointMap['needs uppercase'],
      pointsForContainingNumber: passwordStrengthPointMap['needs number'],
      pointsForContainingSymbol: passwordStrengthPointMap['needs symbol'],
    },
  );

  // Now we can deconstruct this point value quickly to figure out what went wrong
  const potentialPasswordStrengthFailures: PasswordFailureReason[] =
    Object.keys(passwordStrengthPointMap) as PasswordFailureReason[];
  const passwordStrengthSuccesses: PasswordFailureReason[] = [];

  Object.values(passwordStrengthPointMap)
    .sort((a, b) => b - a)
    .forEach((pointValue) => {
      if (passwordStrength >= pointValue) {
        passwordStrength %= pointValue;
        passwordStrengthSuccesses.push(
          potentialPasswordStrengthFailures[
            Object.values(passwordStrengthPointMap).indexOf(pointValue)
          ],
        );
      }
    });

  // Remove all the qualifications that were met
  const passwordStrengthFailures = potentialPasswordStrengthFailures.filter(
    (reason) => passwordStrengthSuccesses.indexOf(reason) < 0,
  );

  return passwordFailures.concat(passwordStrengthFailures);
}
